/**
 * @file SalesManagerRoutes.tsx
 * @module app/routing/SalesManagerRoutes
 * @description Defines role-specific SalesManagerRoutes route mappings, protecting UTrade Admin screens and selecting the appropriate application views.
 *
 * @author Rocket Technologies Ltd
 * @copyright 2026 Rocket Technologies Ltd. All rights reserved.
 * @since 1.1.1
 * @version 2.1.2
 */

import { FC, Suspense } from "react";
import { Navigate, Route, Routes } from "react-router-dom";
import TopBarProgress from "react-topbar-progress-indicator";
import { getCSSVariableValue } from "../../_admin/assets/ts/_utils";
import { WithChildren } from "../../_admin/helpers";
import { MasterLayout } from "../../_admin/layout/MasterLayout";
import CustomerAssignment from "../modules/sales-management/sales-manager/customer-assignment/customer-assignment";
import Dashboard from "../modules/sales-management/sales-manager/dashboard/dashboard";
import RepDetails from "../modules/sales-management/sales-manager/sales-representative/rep-details";
import SalesRepresentative from "../modules/sales-management/sales-manager/sales-representative/sales-representative";
import TargetManagement from "../modules/sales-management/sales-manager/target-management/target-management";
import Orders from "../modules/sales-management/sales-manager/orders/orders";
import Reports from "../modules/sales-management/sales-manager/reports/reports";
import Notifications from "../modules/sales-management/sales-manager/notifications";
import CustomerProfile from "../modules/customers/customer-profile";
import AddNewOrderSubAdmin from "../modules/orders-delivery/pages/operations/add-new-order-assist";
import OrderDetails from "../modules/orders-delivery/pages/order-details/order-details";
import EditOrderDetails from "../modules/orders-delivery/pages/order-details/edit-order-details";
import { PageLink, PageTitle } from "../../_admin/layout/core";

const salesManagementManagerBreadCrumbs: Array<PageLink> = [
  {
    title: 'Sales Management',
    path: '/sales-management',
    isSeparator: false,
    isActive: false,
  },
  {
    title: 'Sales Manager',
    path: '/sales-management/manager',
    isSeparator: false,
    isActive: false,
  },
  {
    title: '',
    path: '',
    isSeparator: true,
    isActive: false,
  },
];
const salesManagementManagerSalesRepBreadCrumbs: Array<PageLink> = [
  {
    title: 'Sales Management',
    path: '/sales-management',
    isSeparator: false,
    isActive: false,
  },
  {
    title: 'Sales Manager',
    path: '/sales-management/manager',
    isSeparator: false,
    isActive: false,
  },
  {
    title: 'Sales Representative',
    path: '/sales-management/manager/sales-representative',
    isSeparator: false,
    isActive: false,
  },
  {
    title: '',
    path: '',
    isSeparator: true,
    isActive: false,
  },
];
const salesManagementManagerOrdersBreadcrumbs: Array<PageLink> = [
  {
    title: 'Sales Management',
    path: '/sales-management',
    isSeparator: false,
    isActive: false,
  },
  {
    title: 'Sales Manager',
    path: '/sales-management/manager',
    isSeparator: false,
    isActive: false,
  },
  {
    title: 'Orders',
    path: '/orders',
    isSeparator: false,
    isActive: false,
  },
  {
    title: '',
    path: '',
    isSeparator: true,
    isActive: false,
  },
];
const salesManagementManagerCustomerAssignmentBreadcrumbs: Array<PageLink> = [
  {
    title: 'Sales Management',
    path: '/sales-management',
    isSeparator: false,
    isActive: false,
  },
  {
    title: 'Sales Manager',
    path: '/sales-management/manager',
    isSeparator: false,
    isActive: false,
  },
  {
    title: 'Customer Assignment',
    path: '/customers',
    isSeparator: false,
    isActive: false,
  },
  {
    title: '',
    path: '',
    isSeparator: true,
    isActive: false,
  },
];

const SalesManagerRoutes = () => {
  return (
    <Routes>
      <Route element={<MasterLayout />}>
        <Route
          path="auth/*"
          element={<Navigate to="/sales-management/manager/dashboard" />}
        />
        {/* Pages */}
        <Route
          path="sales-management/manager/dashboard"
          element={
            <>
              <PageTitle breadcrumbs={salesManagementManagerBreadCrumbs}>
                Dashboard
              </PageTitle>
              <Dashboard />
            </>
          }
        />
        <Route
          path="sales-management/manager/sales-representative"
          element={
            <SuspensedView>
              <PageTitle breadcrumbs={salesManagementManagerSalesRepBreadCrumbs}>
                Sales Representative
              </PageTitle>
              <SalesRepresentative />
            </SuspensedView>
          }
        />
        <Route
          path="sales-management/manager/sales-representative/rep-details"
          element={
            <SuspensedView>
              <PageTitle breadcrumbs={salesManagementManagerSalesRepBreadCrumbs}>
                Rep Details
              </PageTitle>
              <RepDetails />
            </SuspensedView>
          }
        />
        <Route
          path="sales-management/manager/customer-assignment"
          element={
            <SuspensedView>
              <PageTitle breadcrumbs={salesManagementManagerBreadCrumbs}>
                Customer Assignment
              </PageTitle>
              <CustomerAssignment />
            </SuspensedView>
          }
        />
        <Route
          path="sales-management/manager/target-management"
          element={
            <SuspensedView>
              <PageTitle breadcrumbs={salesManagementManagerBreadCrumbs}>
                Target Management
              </PageTitle>
              <TargetManagement />
            </SuspensedView>
          }
        />
        <Route
          path="sales-management/manager/orders"
          element={
            <SuspensedView>
              <PageTitle breadcrumbs={salesManagementManagerBreadCrumbs}>
                Orders
              </PageTitle>
              <Orders />
            </SuspensedView>
          }
        />
        {/* <Route
          path="sales-management/manager/report"
          element={
            <SuspensedView>
              <PageTitle breadcrumbs={salesManagementManagerBreadCrumbs}>
                Reports
              </PageTitle>
              <Orders />
              <Reports />
            </SuspensedView>
          }
        />
        <Route
          path="sales-management/manager/notifications"
          element={
            <SuspensedView>
              <PageTitle breadcrumbs={salesManagementManagerBreadCrumbs}>
                Notifications
              </PageTitle>
              <Orders />
              <Notifications />
            </SuspensedView>
          }
        /> */}
        <Route
          path="customers/customer-profile"
          element={
            <SuspensedView>
              <PageTitle breadcrumbs={salesManagementManagerCustomerAssignmentBreadcrumbs}>
                Customer Profile
              </PageTitle>
              <CustomerProfile />
            </SuspensedView>
          }
        />
        <Route
          path="/orders/add-new-order-assistAdmin"
          element={
            <>
              <PageTitle breadcrumbs={salesManagementManagerOrdersBreadcrumbs}>
                Add New Order
              </PageTitle>
              <AddNewOrderSubAdmin />
            </>
          }
        />
        <Route
          path="/orders/order-details"
          element={
            <>
              <PageTitle breadcrumbs={salesManagementManagerOrdersBreadcrumbs}>
                Order Details
              </PageTitle>
              <OrderDetails />
            </>
          }
        />
        <Route
          path="/orders/edit-order-details"
          element={
            <>
              <PageTitle breadcrumbs={salesManagementManagerOrdersBreadcrumbs}>
                Edit Order Details
              </PageTitle>
              <EditOrderDetails />
            </>
          }
        />
      </Route>
    </Routes>
  );
};
const SuspensedView: FC<WithChildren> = ({ children }) => {
  const baseColor = getCSSVariableValue("--bs-primary");
  TopBarProgress.config({
    barColors: { "0": baseColor },
    barThickness: 1,
    shadowBlur: 5,
  });
  return <Suspense fallback={<TopBarProgress />}>{children}</Suspense>;
};
export { SalesManagerRoutes };
